/**
 * useUpdateFieldValue — Mutation for field value saves with optimistic updates.
 * Phase 3B: enhanced with onMutate/onError/onSettled for optimistic cache updates
 * on the scenario-tree query cache.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { updateFieldValue } from "../../api/fields";
import { SCENARIO_TREE_KEY, type EditorState } from "../queries/useScenarioTree";
import { SECTION_DATA_KEY } from "../queries/useSectionLoader";

interface UpdateFieldValueParams {
  id: number;
  value: unknown;
  scenarioId: number;
  modelDataId?: string;
  fieldName?: string;
}

export function useUpdateFieldValue() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, value }: UpdateFieldValueParams) =>
      updateFieldValue(id, value),

    onMutate: async (variables: UpdateFieldValueParams) => {
      const { scenarioId, modelDataId, fieldName, value } = variables;
      if (!modelDataId || !fieldName) return {};

      await queryClient.cancelQueries({
        queryKey: [SCENARIO_TREE_KEY, scenarioId],
      });

      const previousState = queryClient.getQueryData<EditorState>([
        SCENARIO_TREE_KEY,
        scenarioId,
      ]);

      if (previousState) {
        queryClient.setQueryData<EditorState>(
          [SCENARIO_TREE_KEY, scenarioId],
          (old) => {
            if (!old) return old;
            const item = old.Input.tree.items[modelDataId];
            if (!item?.fields?.[fieldName]) return old;

            const field = item.fields[fieldName];
            const updatedField = { ...field };
            if (updatedField.currentValue) {
              updatedField.currentValue = {
                ...updatedField.currentValue,
                value,
              };
            } else if (updatedField.value) {
              updatedField.value = { ...updatedField.value, value };
            }

            return {
              ...old,
              Input: {
                ...old.Input,
                tree: {
                  ...old.Input.tree,
                  items: {
                    ...old.Input.tree.items,
                    [modelDataId]: {
                      ...item,
                      fields: { ...item.fields, [fieldName]: updatedField },
                    },
                  },
                },
              },
            };
          }
        );
      }

      return { previousState, scenarioId };
    },

    onError: (_err, _variables, context) => {
      if (context?.previousState && context?.scenarioId) {
        queryClient.setQueryData(
          [SCENARIO_TREE_KEY, context.scenarioId],
          context.previousState
        );
      }
    },

    onSettled: (_data, _error, variables) => {
      // Only invalidate section-data cache so stale sections are refetched on next
      // open.  Do NOT invalidate SCENARIO_TREE_KEY: its queryFn returns an empty
      // initial state, so an invalidation-triggered refetch would wipe the
      // lazily-loaded tree and cause model-data items to disappear.
      queryClient.invalidateQueries({
        queryKey: [SECTION_DATA_KEY, variables.scenarioId],
      });
    },
  });
}
